import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Globe, Search } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/States';
import { useDebounced } from '@/hooks/useDebounced';
import { DatasetDetailDialog } from '@/components/datasets/DatasetDetailDialog';
import { formatNumber, formatRelative } from '@/lib/format';

const CATEGORIES = [
  { value: '', label: 'Semua kategori' },
  { value: 'e-commerce', label: 'E-commerce' },
  { value: 'berita', label: 'Berita' },
  { value: 'lowongan-kerja', label: 'Lowongan Kerja' },
  { value: 'properti', label: 'Properti' },
  { value: 'kuliner', label: 'Kuliner' },
  { value: 'media-sosial', label: 'Media Sosial' },
  { value: 'keuangan', label: 'Keuangan' },
  { value: 'akademik', label: 'Akademik' },
  { value: 'lainnya', label: 'Lainnya' },
];

const CATEGORY_TONES = {
  'e-commerce': 'accent',
  berita: 'warning',
  'lowongan-kerja': 'success',
  properti: 'brand',
  kuliner: 'neutral',
  'media-sosial': 'info',
};

/** Katalog Data Publik - dataset yang dibagikan pengguna lain. */
export const DatasetsPage = () => {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const debouncedSearch = useDebounced(search, 350);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['datasets', { search: debouncedSearch, category }],
    queryFn: () =>
      api.get('/datasets', { params: { search: debouncedSearch, category, limit: 24 } }),
    placeholderData: (previous) => previous,
  });

  const datasets = data?.data ?? [];
  const [detail, setDetail] = useState(null);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cari dataset publik…"
          iconLeft={Search}
          aria-label="Cari dataset"
          className="sm:max-w-sm"
        />
        <Select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          options={CATEGORIES}
          aria-label="Filter kategori"
          className="sm:w-52"
        />
      </div>

      {isError ? (
        <Card>
          <ErrorState message={error?.message} onRetry={refetch} />
        </Card>
      ) : isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="p-5">
              <Skeleton className="h-5 w-24 rounded-full" />
              <Skeleton className="mt-3 h-5 w-3/4" />
              <Skeleton className="mt-2 h-3 w-1/2" />
              <Skeleton className="mt-4 h-9 w-full" />
            </Card>
          ))}
        </div>
      ) : datasets.length === 0 ? (
        <Card>
          <EmptyState
            icon={Globe}
            title={search || category ? 'Tidak ada dataset yang cocok' : 'Belum ada dataset publik'}
            description={
              search || category
                ? 'Coba kata kunci atau kategori yang lain.'
                : 'Dataset yang dipublikasikan pengguna akan muncul di sini dan bisa dipakai ulang siapa saja.'
            }
          />
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {datasets.map((dataset) => (
            <Card
              key={dataset.id}
              className="flex cursor-pointer flex-col transition-shadow hover:shadow-md"
              onClick={() => setDetail(dataset)}
            >
              <CardBody className="flex flex-1 flex-col">
                <Badge tone={CATEGORY_TONES[dataset.category] ?? 'neutral'} size="sm">
                  {CATEGORIES.find((c) => c.value === dataset.category)?.label ?? dataset.category}
                </Badge>

                <h3 className="mt-3 text-[15px] font-bold text-[var(--text-primary)]">
                  {dataset.title}
                </h3>
                <p className="mt-0.5 text-xs text-[var(--text-muted)]">{dataset.sourceDomain}</p>

                {dataset.description && (
                  <p className="mt-2 line-clamp-2 text-sm text-[var(--text-secondary)]">
                    {dataset.description}
                  </p>
                )}

                <div className="mt-4 flex items-center justify-between border-t border-[var(--border-subtle)] pt-3 text-xs text-[var(--text-muted)]">
                  <span className="tabular">{formatNumber(dataset.rowCount)} baris</span>
                  <span>Diperbarui {formatRelative(dataset.dataUpdatedAt)}</span>
                </div>

                <Button
                  variant="secondary"
                  size="sm"
                  icon={Download}
                  className="mt-3 w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDetail(dataset);
                  }}
                >
                  Lihat, Inspek & Gunakan
                </Button>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {detail && <DatasetDetailDialog dataset={detail} onClose={() => setDetail(null)} />}
    </div>
  );
};
